import type { EpgDate, EpgDateEx, EpgDateString } from './index.h'

import { addDays, subDays } from 'date-fns'
import { DEFAULT_WEEK_DURATION, INVALID_DATE } from './index.h'

/**
 * Compare equality of two epg dates
 */
export const isSameDate = (a: EpgDate | Date) => (b: EpgDate | Date) => {
  const x = a instanceof Date ? fromDate(a) : a
  const y = b instanceof Date ? fromDate(b) : b
  return x.year === y.year && x.month === y.month && x.day === y.day
}

/**
 * Checks, if the EpgDate is today,
 * cannot be simply curried `isSameDate(new Date())`, because `new Date()` should be called on every check
 */
export const isToday = (epgDate: EpgDate) => isSameDate(new Date())(epgDate)

/**
 * Checks, if given string is correctly formatted EpgDateString
 */
export const isEpgDateString = (x: unknown): x is EpgDateString =>
  typeof x === 'string' && /^\d{4}-\d{1,2}-\d{1,2}$/.test(x)

/**
 * Clone Date object
 */
export const fromDate = (date: Date): EpgDate => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  return { year, month, day }
}

/**
 * Deserialization function, not for UI
 */
export const fromString = (x: EpgDateString): EpgDateEx => {
  if (isEpgDateString(x)) {
    const [year, month, day] = x.split('-').map(Number)
    return fromEpgDate({ year, month: month - 1, day })
  }
  return INVALID_DATE // cannot throw an error here
}

/**
 * Convert EpgDate to timestamp in ms
 */
export const toTimestamp = (x: EpgDate): number =>
  new Date(x.year, x.month, x.day).getTime()

/**
 * Serialization function, not for UI
 */
export const toString = (x: EpgDate): EpgDateString => {
  const year = String(x.year)
  const month = String(x.month + 1).padStart(2, '0')
  const day = String(x.day).padStart(2, '0')
  return `${year}-${month}-${day}` as EpgDateString
}

/**
 * Clone other EpgDate with actualization `isToday`, `isYesterday`, and `isTomorrow` flags
 */
export const fromEpgDate = (x: EpgDate): EpgDateEx => {
  const { year, month, day } = x

  const today = new Date()
  const yesterday = subDays(today, 1)
  const tomorrow = addDays(today, 1)

  return {
    year,
    month,
    day,
    isToday: isSameDate(today)(x),
    isYesterday: isSameDate(yesterday)(x),
    isTomorrow: isSameDate(tomorrow)(x),
  }
}

/**
 * Get current date
 */
export const getToday = (): EpgDateEx => {
  return fromEpgDate(fromDate(new Date()))
}

/**
 * Generate week in future from the given date
 */
export const generateWeek = (
  from: EpgDateEx,
  duration = DEFAULT_WEEK_DURATION
): EpgDateEx[] => {
  const week = [from]
  const date = new Date(from.year, from.month, from.day)

  // starts from 1 because we already have the first day
  for (let i = 1; i < duration; i++) {
    date.setDate(date.getDate() + 1)
    week.push(fromEpgDate(fromDate(date)))
  }

  return week
}
